import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, MapPin, Briefcase, Users } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Avatar } from '../ui/avatar';

interface MostConnectedMembersProps {
  className?: string;
}

interface Member {
  id: string;
  name: string;
  image: string;
  city: string;
  jobTitle?: string;
  company?: string;
  connectionCount: number;
}

const MostConnectedMembers: React.FC<MostConnectedMembersProps> = ({ className }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMostConnectedMembers = async () => {
      try {
        setIsLoading(true);

        // Fetch most connected users using the RPC function
        const { data: mostConnected, error: connError } = await supabase.rpc(
          'get_most_connected_users',
          { limit_count: 5 }
        );

        if (connError) throw connError;

        // Extract user IDs and fetch their profiles
        const ids = mostConnected.map((u: any) => u.user_id);
        const { data: profiles = [], error: profilesError } = await supabase
          .from('users')
          .select('auth_id, id, full_name, avatar_url, city, company, job_type')
          .in('id', ids);

        if (profilesError) throw profilesError;

        // Maintain original order by mapping from RPC result
        const idToProfile = Object.fromEntries(profiles.map((p) => [p.id, p]));
        const sortedMembers = mostConnected
          .map((entry) => {
            const profile = idToProfile[entry.user_id];
            if (!profile) return null;
            return {
              id: profile.auth_id,
              name: profile.full_name,
              image:
                profile.avatar_url ||
                'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png',
              city: profile.city,
              jobTitle: profile.job_type,
              company: profile.company,
              connectionCount: entry.total_connections,
            };
          })
          .filter(Boolean) as Member[];

        setMembers(sortedMembers);
      } catch (error) {
        console.error('Error fetching most connected members:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMostConnectedMembers();
  }, []);

  const handleMemberClick = (memberId: string) => {
    navigate(`/dashboard/members/${memberId}`);
  };

  const handleViewAllClick = () => {
    navigate('/dashboard/match');
  };

  if (isLoading) {
    return (
      <div className={cn('bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 animate-pulse', className)}>
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-64 bg-dark-300 rounded"></div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="h-10 w-10 rounded-full bg-dark-300"></div>
              <div className="flex-1">
                <div className="h-4 w-32 bg-dark-300 rounded mb-2"></div>
                <div className="h-3 w-24 bg-dark-300 rounded"></div>
              </div>
              <div className="h-6 w-16 bg-dark-300 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (members.length === 0) return null;

  return (
    <div className={cn('bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6', className)}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Trophy className="h-5 w-5 text-gold-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">Most Connected</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={handleViewAllClick} className="text-dark-800 hover:text-white">
          View All Players
        </Button>
      </div>

      <div className="space-y-3">
        {members.map((member, index) => (
          <div
            key={member.id}
            onClick={() => handleMemberClick(member.id)}
            className="flex items-center p-3 rounded-lg bg-dark-300 hover:bg-dark-400 transition-colors cursor-pointer overflow-hidden"
          >
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <div className="relative">
                {index < 3 && (
                  <div
                    className={cn(
'absolute -top-3 -left-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold',

                      index === 0
                        ? 'bg-gold-400 text-dark-50'
                        : index === 1
                        ? 'bg-silver-400 text-dark-50'
                        : 'bg-bronze-400 text-dark-50'
                    )}
                  >
                    {index + 1}
                  </div>
                )}
                <Avatar src={member.image} alt={member.name} size={40} className="border-2 border-dark-400" />
              </div>

              <div className="min-w-0">
                <h4 className="font-medium text-white truncate text-sm sm:text-base">{member.name}</h4>
                <div className="flex flex-col xs:flex-row xs:items-center text-dark-800 text-xs">
                  {member.city && (
                    <div className="flex items-center">
                      <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{member.city}</span>
                    </div>
                  )}
                  {member.jobTitle && member.company && (
                    <>
                      <span className="hidden xs:inline mx-1">•</span>
                      <div className="flex items-center">
                        <Briefcase className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">
                          {member.jobTitle} @ {member.company}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center text-gold-400 ml-2 w-12 flex-shrink-0 justify-end">
              <Users className="h-4 w-4 mr-1" />
              <span className="font-medium text-sm sm:text-base whitespace-nowrap">
                {member.connectionCount}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MostConnectedMembers;
